/**
 * Burned-in captions: a scene's `caption` text, word-wrapped at the bottom of the output frame on a
 * translucent box. Drawn from font outlines (not system fonts), so preview and render match.
 */

import type { Ctx2D } from "./canvas";
import { toPath2D, type Font } from "./fonts";
import type { Captions } from "./project.gen";

interface Layout {
  key: string;
  paths: Path2D[];
  box: { x: number; y: number; w: number; h: number };
}

let cache: Layout | null = null;

export function layoutCaption(font: Font, text: string, c: Captions, W: number, H: number): Layout {
  const key = `${text}|${c.size}|${W}|${H}`;
  if (cache?.key === key) return cache;
  const px = W / 1920;
  const size = c.size * px;
  const maxW = W * 0.8;
  const lines: string[] = [];
  for (const para of text.split("\n")) {
    let cur = "";
    for (const word of para.split(/\s+/).filter(Boolean)) {
      const t = cur ? `${cur} ${word}` : word;
      if (cur && font.getAdvanceWidth(t, size) > maxW) {
        lines.push(cur);
        cur = word;
      } else cur = t;
    }
    if (cur) lines.push(cur);
  }
  const lineH = size * 1.25;
  const padX = size * 0.6;
  const padY = size * 0.3;
  const widths = lines.map((l) => font.getAdvanceWidth(l, size));
  const boxW = Math.max(...widths, 0) + 2 * padX;
  const boxH = lines.length * lineH + 2 * padY;
  const box = { x: (W - boxW) / 2, y: H - H * 0.06 - boxH, w: boxW, h: boxH };
  const asc = (font.ascender / font.unitsPerEm) * size;
  const paths = lines.map((l, i) => toPath2D(font.getPath(l, (W - widths[i]) / 2, box.y + padY + i * lineH + (lineH - size) / 2 + asc, size)));
  cache = { key, paths, box };
  return cache;
}

export function drawCaption(ctx: Ctx2D, font: Font, text: string, c: Captions, W: number, H: number): void {
  if (!text.trim()) return;
  const L = layoutCaption(font, text, c, W, H);
  ctx.save();
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.fillStyle = c.background;
  const r = Math.min(L.box.h / 2, 10 * (W / 1920));
  ctx.beginPath();
  ctx.roundRect(L.box.x, L.box.y, L.box.w, L.box.h, r);
  ctx.fill();
  ctx.fillStyle = c.color;
  for (const p of L.paths) ctx.fill(p);
  ctx.restore();
}
